/** document-svc upload + classification routes (wired in Phase 5.6). */

import type {
  ClassificationActionRequest,
  ClassificationStatus,
  Document,
  DocumentStatus,
  PresignedUpload,
  PresignedUploadRequest,
  Uuid,
} from "@re-platform/types";

import type { ApiClient } from "../client";

/** Mirrors document-svc's DocumentDownloadOut. */
export interface DocumentDownload {
  document_id: Uuid;
  /** Presigned S3 GET — open it, don't fetch it (see `openDocument`). */
  download_url: string;
  /** Seconds until the URL stops working. */
  expires_in: number;
  file_name: string | null;
  mime_type: string | null;
}

export const documentService = {
  presignedUrl(client: ApiClient, body: PresignedUploadRequest) {
    return client.request<PresignedUpload>("/document/presigned-url", {
      method: "POST",
      body,
    });
  },

  list(
    client: ApiClient,
    params?: {
      client_id?: Uuid;
      status?: DocumentStatus;
      classification_status?: ClassificationStatus;
      limit?: number;
      offset?: number;
      /** Staff only (403 for clients): also return DISCARDED documents, the
       *  agent's history of what was uploaded by mistake or replaced. */
      include_discarded?: boolean;
    },
  ) {
    return client.request<Document[]>("/document/documents", { query: params });
  },

  get(client: ApiClient, documentId: Uuid) {
    return client.request<Document>(`/document/documents/${documentId}`);
  },

  /**
   * Short-lived presigned GET for an uploaded file. Same scope rules as `get`,
   * enforced server-side. 409s while an upload is still PENDING_UPLOAD.
   *
   * Pair with `openDocument` — do NOT fetch() the returned URL, see below.
   */
  download(client: ApiClient, documentId: Uuid) {
    return client.request<DocumentDownload>(
      `/document/documents/${documentId}/download`,
    );
  },

  /** Labeler / mismatch-banner action — relabel or confirm_declared. */
  classify(
    client: ApiClient,
    documentId: Uuid,
    body: ClassificationActionRequest,
  ) {
    return client.request<Document>(
      `/document/documents/${documentId}/classification`,
      {
        method: "POST",
        body,
      },
    );
  },

  /**
   * "I uploaded this by mistake" (spec 017). The document stops counting
   * everywhere and leaves the client's view; every checklist item it filled
   * reads "Needed" again. Nothing is deleted — the agent keeps it as history.
   * Works on documents filling a slot, unlike `remove`.
   *
   * 409 when it fills a REQUIRED item of a COMPLETE application. Not offered on
   * signed links (no such route).
   */
  discard(client: ApiClient, documentId: Uuid) {
    return client.request<Document>(
      `/document/documents/${documentId}/discard`,
      { method: "POST" },
    );
  },

  /**
   * Soft delete, refused (409) for anything filling a checklist slot. No longer
   * used by the apps — "Remove" now calls `discard`, which handles slotted
   * documents and records who removed it. Kept for API compatibility.
   */
  remove(client: ApiClient, documentId: Uuid) {
    return client.request<void>(`/document/documents/${documentId}`, {
      method: "DELETE",
    });
  },
};

/**
 * Fetch a download URL and hand it to the browser.
 *
 * Deliberately a plain navigation (`window.open`), never `fetch()`: a direct
 * navigation to S3 needs no bucket CORS rule, whereas fetching the same URL
 * from the SPA origin would — and the docs bucket's CORS only covers the
 * upload PUT. Navigating also lets the browser do what it is good at (stream
 * large files, honour the signed Content-Disposition, show its own progress)
 * instead of buffering the whole file into a blob in memory.
 *
 * Opened via a synthetic anchor rather than assigning `location`, so the
 * current page (and any unsaved form state on it) survives the download.
 */
export async function openDocument(
  client: ApiClient,
  documentId: Uuid,
): Promise<void> {
  const { download_url } = await documentService.download(client, documentId);
  const a = window.document.createElement("a");
  a.href = download_url;
  a.rel = "noopener";
  a.target = "_blank";
  window.document.body.appendChild(a);
  a.click();
  a.remove();
}

/**
 * PUT the file to the presigned URL. Plain fetch — the URL is self-signed and
 * the headers returned by the backend are part of the signature.
 */
export async function uploadToPresignedUrl(
  presigned: PresignedUpload,
  file: Blob,
): Promise<void> {
  const response = await fetch(presigned.upload_url, {
    method: presigned.method,
    headers: presigned.headers,
    body: file,
  });
  if (!response.ok) {
    throw new Error(`S3 upload failed with status ${response.status}`);
  }
}
